import { useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

const LINE_URL = 'https://lin.ee/ZHvBTbt';
const INSTAGRAM_URL =
  'https://www.instagram.com/inc.universe?igsh=MTRhaXYwbTdyY3R2cQ%3D%3D&utm_source=qr';
const MAILTO = 'mailto:universe.inc42@gmail.com';

const TRANSITION_MS = 320;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

/** 募集要項モーダル：開閉アニメーション・body スクロールロック */
export default function RecruitmentModal({ isOpen, onClose }: Props) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  // 開く：DOM を出してから次フレームで visible（トランジション発火）
  // 閉じる：visible を落としてから DOM を外す
  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
    const timer = window.setTimeout(() => setMounted(false), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!mounted) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mounted, handleKeyDown]);

  if (!mounted) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recruitment-modal-title"
    >
      {/* オーバーレイ */}
      <button
        type="button"
        className={`absolute inset-0 cursor-default bg-[#0B1A2A]/65 backdrop-blur-[2px] transition-opacity duration-300 ease-out motion-reduce:transition-none ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-label="モーダルを閉じる"
      />

      <div
        className={`relative z-10 flex max-h-[min(92vh,820px)] w-full max-w-lg flex-col overflow-hidden rounded-xl border shadow-2xl transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none motion-reduce:transform-none ${
          visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-[0.97] opacity-0'
        }`}
        style={{
          backgroundColor: '#FAF8F3',
          borderColor: 'rgba(168, 205, 217, 0.45)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
          style={{ color: '#2C2C2C' }}
          onClick={onClose}
          aria-label="閉じる"
        >
          <i className="ri-close-line text-2xl" aria-hidden={true} />
        </button>

        <div className="overflow-y-auto overscroll-contain px-6 pb-8 pt-14 sm:px-8 sm:pb-10 sm:pt-16">
          <h2
            id="recruitment-modal-title"
            className="font-serif-display mb-2 text-center leading-tight"
            style={{
              color: '#1A1A1A',
              fontSize: 'clamp(22px, 4vw, 28px)',
              fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
              fontWeight: 400,
            }}
          >
            募集要項
          </h2>

          <dl className="space-y-5 text-sm sm:text-[15px]">
            <div className="border-b border-solid pb-4" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                職種
              </dt>
              <dd className="font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                営業 / コンサルティング / マーケティング
              </dd>
            </div>
            <div className="border-b border-solid pb-4" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                業務内容
              </dt>
              <dd className="space-y-3 font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                <p>
                  営業活動（対面・電話）を中心に、
                  <br />
                  お客様への提案や課題解決を行います。
                </p>
                <p>
                  経験やスキルに応じて、
                  <br />
                  人材育成・マネジメント・マーケティング業務にも携わっていただきます。
                </p>
              </dd>
            </div>
            <div className="border-b border-solid pb-4" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                雇用形態
              </dt>
              <dd className="font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                正社員 / アルバイト / 業務委託
              </dd>
            </div>
            <div className="border-b border-solid pb-4" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                応募資格
              </dt>
              <dd className="space-y-2 font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                <p>学歴・年齢・経験不問</p>
                <p>未経験歓迎／やる気・成長意欲を重視します</p>
              </dd>
            </div>
            <div className="border-b border-solid pb-4" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                給与
              </dt>
              <dd className="space-y-2 font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                <p>月給 25万円〜（経験・能力を考慮の上、決定）</p>
                <ul className="list-none space-y-1 pl-0 text-[13px] sm:text-sm">
                  <li>※成果に応じてインセンティブあり</li>
                  <li>※キャリアアップに応じて昇給あり</li>
                  <li>※業務委託の場合は別途ご相談</li>
                </ul>
              </dd>
            </div>
            <div className="border-b border-solid pb-4" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                勤務時間
              </dt>
              <dd className="space-y-1 font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                <p>10:00〜19:00（休憩あり）</p>
                <p className="text-[13px] sm:text-sm">※配属先や業務内容により変動あり</p>
              </dd>
            </div>
            <div className="border-b border-solid pb-4" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                勤務地
              </dt>
              <dd className="font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                大阪府内（プロジェクト先により異なる）
              </dd>
            </div>
            <div className="border-b border-solid pb-4" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                休日・休暇
              </dt>
              <dd className="space-y-1 font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                <p>週休2日制（シフト制）</p>
                <p>GW・夏季・年末年始休暇あり</p>
              </dd>
            </div>
            <div className="border-b border-solid pb-4" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                福利厚生
              </dt>
              <dd className="font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                <ul className="list-disc space-y-1.5 pl-5">
                  <li>研修制度あり（未経験でも安心）</li>
                  <li>インセンティブ制度</li>
                  <li>キャリアアップ制度</li>
                  <li>独立支援制度あり</li>
                </ul>
              </dd>
            </div>
            <div>
              <dt className="mb-1 font-medium" style={{ color: '#A8CDD9', fontFamily: "'Noto Sans JP', sans-serif" }}>
                選考フロー
              </dt>
              <dd className="font-light leading-relaxed" style={{ color: '#3A3A3A', fontFamily: "'Noto Sans JP', sans-serif" }}>
                書類選考→面接→内定
              </dd>
            </div>
          </dl>

          <div className="mt-10 border-t pt-8" style={{ borderColor: 'rgba(168,205,217,0.35)' }}>
            <p className="mb-4 text-center text-xs font-light" style={{ color: '#888', fontFamily: "'Noto Sans JP', sans-serif" }}>
              ご応募・お問い合わせは各種チャネルからどうぞ
            </p>
            <div className="flex flex-col gap-3">
              <a
                href={LINE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-btn-gold flex w-full items-center justify-center gap-2 px-4 py-3 text-sm font-light text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#B89B4A', fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                <i className="ri-line-fill text-lg" aria-hidden={true} />
                LINE
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-pill-outline flex w-full items-center justify-center gap-2 border px-4 py-3 text-sm font-light transition-colors hover:bg-black/5"
                style={{
                  borderColor: 'rgba(43, 43, 43, 0.25)',
                  color: '#2C2C2C',
                  fontFamily: "'Noto Sans JP', sans-serif",
                }}
              >
                <i className="ri-instagram-line text-lg" aria-hidden={true} />
                Instagram
              </a>
              <a
                href={MAILTO}
                className="btn-pill-outline flex w-full items-center justify-center gap-2 border px-4 py-3 text-sm font-light transition-colors hover:bg-[rgba(61,107,140,0.06)]"
                style={{
                  borderColor: 'rgba(61, 107, 140, 0.35)',
                  color: '#2C4A5E',
                  fontFamily: "'Noto Sans JP', sans-serif",
                }}
              >
                <i className="ri-mail-line text-lg" aria-hidden={true} />
                メールでお問い合わせ
              </a>
            </div>
            <p className="mt-3 text-center text-[10px] font-light" style={{ color: '#aaa', fontFamily: "'Noto Sans JP', sans-serif" }}>
              メールはお使いのメールアプリが開きます
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
